import {DataStatus, Segment} from 'platform/components';
import {
  Align,
  Box,
  Heading,
  Hide,
  HStack,
  Show,
  Space,
  Text,
  useElementSize,
  VStack,
} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import styled, {useTheme} from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {currencyCodes, getSafely, suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../i18n/i18n';
import {CarTooltip} from '../../pages/StatisticsPage/components/Chart/components/CarTooltip';
import {MY_VEHICLE_COLOR} from '../../pages/StatisticsPage/components/Chart/consts/myVehicleColor';
import {ExtendedStatisticsCar} from '../../pages/StatisticsPage/components/Chart/types/ExtendedStatisticsCar';
import {formatChartPriceLabel} from '../../pages/StatisticsPage/components/Chart/utils/formatChartPriceLabel';
import {PriceReportType} from '../../types/PriceReportType';
import {isSoldIn30Days} from '../../utils/isSoldIn30Days';
import {BandColumns} from './components/BandColumns';
import {DistributionAxis} from './components/DistributionAxis';
import {DistributionMarker} from './components/DistributionMarker';
import {QuintileZones} from './components/QuintileZones';
import {MOBILE_DE_PRICE_BANDS} from './consts/MOBILE_DE_PRICE_BANDS';
import {DistributionAxisMode, useDistributionAxisMode} from './hooks/useDistributionAxisMode';
import {buildBandAxisTicks} from './utils/buildBandAxisTicks';
import {getPriceBandIndex, getPriceBands} from './utils/getPriceBands';
import {interpolatePaletteColors} from './utils/interpolatePaletteColors';
import {isPriceBandAxisAvailable} from './utils/isPriceBandAxisAvailable';

interface PriceDistributionChartProps extends TestIdProps {
  priceReport: PriceReportType;
  /** Vehicles to distribute — one unit rectangle each (advertised and/or sold). */
  cars?: ExtendedStatisticsCar[];
  /**
   * Plain advertised prices to distribute — used on the dashboard, where the chart
   * mirrors the baseline price chart's frozen dataset (prices only, so no hover).
   * Ignored when {@link cars} is provided.
   */
  prices?: number[];
  /** The vehicle's own recommended price (CZK); drawn as the "Tento vůz" marker. */
  myPrice?: number;
  isLoading?: boolean;
  /** Grow the chart to fill its container's height (statistics page) instead of a fixed height. */
  fillHeight?: boolean;
  /** Statistics-page charts render without their own title and description. */
  isTitleHidden?: boolean;
  /**
   * 'thisVehicle' (default, dashboard): blue marker labelled “Tento vůz”.
   * 'recommendedPrice' (statistics): near-black marker labelled with the price itself.
   */
  markerVariant?: 'thisVehicle' | 'recommendedPrice';
}

/** Fixed height on the dashboard; a fallback until the container is measured when filling. */
const CHART_HEIGHT = 322;
/** Minimum chart height as a spacing-scale unit (≈ CHART_HEIGHT). */
const CHART_MIN_HEIGHT_UNITS = 80;
const PADDING_TOP = 56;
const PADDING_BOTTOM = 56;
const SLOT_COUNT = 50;
const QUINTILE_COUNT = 5;
/** Below this container width the SVG scales down uniformly via the viewBox. */
const MIN_CHART_WIDTH = 560;
/** Unit squares for small samples; thin lines once a slot holds hundreds of cars. */
const MAX_UNIT_HEIGHT = 13;
const MIN_UNIT_HEIGHT = 0.6;
const CZK_PER_THOUSAND = 1000;
const TENTHS = 10;
const PERCENT = 100;
const AXIS_LABEL_EDGE_INSET = 16;
/** The pill sits just inside the tinted band zone so it never covers the % labels. */
const MARKER_PILL_TOP_INSET = 6;
/** Top edge of the tinted zone/columns area, above the plot. */
const ZONE_TOP_OFFSET = 24;
const LEGEND_WIDTH = 200;
/** Gap between the hovered unit and the car tooltip above it. */
const TOOLTIP_MARGIN = 6;
/** Minimum room (SVG units) a boundary label needs before labels start thinning. */
const BAND_AXIS_LABEL_MIN_WIDTH = 48;
/** Fewer bands than this cannot spread a distribution — the band axis is unusable. */
const MIN_BAND_COUNT = 2;

/** Gap between stacked units shrinks as the unit height shrinks (spec F1). */
const UNIT_GAP_STEPS = [
  [6, 2.5],
  [3, 1],
  [1.7, 0.45],
] as const;
const MIN_UNIT_GAP = 0.12;
const UNIT_RADIUS_STEPS = [
  [4, 2],
  [2, 1],
] as const;

type CarCategory = 'advertised' | 'sold30' | 'sold90';

const getUnitGap = (unitHeight: number): number =>
  UNIT_GAP_STEPS.find(([threshold]) => unitHeight > threshold)?.[1] ?? MIN_UNIT_GAP;

const getUnitRadius = (unitHeight: number): number =>
  UNIT_RADIUS_STEPS.find(([threshold]) => unitHeight > threshold)?.[1] ?? 0;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

interface DistributionItem {
  price: number;
  /** Null for price-only datasets (dashboard) — such units are not hoverable. */
  car: ExtendedStatisticsCar | null;
}

interface HoveredUnit {
  car: ExtendedStatisticsCar;
  anchorX: number;
  anchorY: number;
}

/**
 * F1 price distribution — every comparable vehicle is one unit rectangle, binned by
 * price into 50 slots and stacked bottom-up; units thin into lines for large samples.
 * Advertised cars carry the orange quintile ramp, sold cars a neutral shade (matching
 * the statistics legend). The price axis is split into fifths labelled with the share
 * of cars, the recommended price sits on the "Tento vůz" marker, and hovering a unit
 * reveals that car in the shared statistics tooltip.
 */
export function PriceDistributionChart(props: PriceDistributionChartProps) {
  const theme = useTheme();
  const formatNumber = useFormatNumber();
  const [ref, containerWidth, containerHeight] = useElementSize<HTMLDivElement>();
  const [tooltipRef, tooltipWidth, tooltipHeight] = useElementSize<HTMLDivElement>();
  const [hovered, setHovered] = useState<HoveredUnit | null>(null);

  // One item per vehicle: full cars (statistics — hoverable) or bare prices
  // (dashboard — the baseline chart's frozen dataset carries no ad ids).
  const carItems: DistributionItem[] = (props.cars ?? [])
    .filter((car) => isNotNil(car.price_with_vat))
    .map((car) => ({price: car.price_with_vat ?? 0, car}));
  const priceItems: DistributionItem[] = (props.prices ?? []).map((price) => ({price, car: null}));
  const sortedItems = (isNotNil(props.cars) ? carItems : priceItems).sort(
    (left, right) => left.price - right.price
  );
  const low = sortedItems[0]?.price ?? 0;
  const high = sortedItems[sortedItems.length - 1]?.price ?? 0;
  const span = high - low;
  const total = sortedItems.length;
  // A distribution needs a price axis to spread over — at least two vehicles with
  // two distinct prices. Anything less renders the empty state instead of a chart.
  const hasEnoughData = total >= 2 && span > 0;

  // The svg unmounts while loading/empty without ever firing onMouseLeave — clear a
  // lingering hover during render (React's derived-state adjustment pattern) so the
  // always-mounted overlay never shows a stale car over the spinner or empty state.
  const isChartShown = !props.isLoading && hasEnoughData;
  if (isNotNil(hovered) && !isChartShown) {
    setHovered(null);
  }

  // With the percentile bands tinted below, every advertised car reads in one orange.
  const advertisedColor = theme.colors.palettes.orange[60][100];
  // Light traffic-light backgrounds of the five price-field bands — cheap end green,
  // expensive end red — so it is obvious at a glance which band a car falls into.
  // Only the palest palette shades (10/20), matching the price gauge's pastel look.
  const bandColors = [
    theme.colors.palettes.green[10][100],
    theme.colors.palettes.green[20][100],
    theme.colors.palettes.yellow[20][100],
    theme.colors.palettes.orange[20][100],
    theme.colors.palettes.red[20][100],
  ];
  // Same colours the statistics chart/legend use: sold ≤30 days is the dark neutral
  // Flag, sold 31–90 days the light one — bumped one shade darker so it stays
  // readable on the tinted band backgrounds.
  const soldRecentColor = theme.colors.palettes.neutral[300][100];
  const soldOldColor = theme.colors.palettes.neutral[50][100];
  // The vehicle's own advertisement — same mint the main statistics chart uses.
  const myAdColor = theme.colors.palettes.green[50][100];
  const markerVariant = props.markerVariant ?? 'thisVehicle';
  const markerColor = match(markerVariant)
    .with('thisVehicle', () => theme.colors.palettes.blue[60][100])
    .with('recommendedPrice', () => MY_VEHICLE_COLOR)
    .exhaustive();
  // The price label matches the other charts' reference-line format (e.g. 295 tis. Kč).
  const recommendedCurrency = props.priceReport.sellingPrice.recommended.currency;
  const currencyCode = currencyCodes.find((code) => code === recommendedCurrency) ?? 'CZK';
  const markerLabel = match(markerVariant)
    .with('thisVehicle', () => i18n.t('entity.overviewStatisticsMileageChart.labels.thisVehicle'))
    .with('recommendedPrice', () =>
      formatChartPriceLabel(props.myPrice ?? 0, currencyCode, {formatNumber})
    )
    .exhaustive();
  // Axis/Δ labels in thousands: CZK rounds to whole thousands, other currencies
  // (euro amounts are ~25× smaller) keep one decimal place.
  const formatThousands = (price: number): string =>
    match(currencyCode)
      .with('CZK', () => formatNumber(Math.round(price / CZK_PER_THOUSAND)) ?? '')
      .otherwise(
        () => formatNumber(Math.round((price / CZK_PER_THOUSAND) * TENTHS) / TENTHS, 1) ?? ''
      );
  // Portal price-band axis (mobile.de) — available on the German market for EUR
  // prices; the user's linear/bands choice is a viewing preference kept in
  // localStorage across reports and sessions. When the whole dataset falls into a
  // single band the distribution has no columns to spread over — the toggle hides
  // and the even scale is used regardless of the stored preference.
  const [axisMode, setAxisMode] = useDistributionAxisMode();
  const isBandAxisAvailable =
    isPriceBandAxisAvailable(props.priceReport.market) && currencyCode === 'EUR';
  const priceBands = isBandAxisAvailable ? getPriceBands(MOBILE_DE_PRICE_BANDS, low, high) : [];
  const isBandAxisUsable = isBandAxisAvailable && priceBands.length >= MIN_BAND_COUNT;
  const isBandMode = isBandAxisUsable && axisMode === 'priceBands';
  const slotCount = isBandMode ? priceBands.length : SLOT_COUNT;
  const axisModeOptions = [
    {value: 'linear', label: i18n.t('entity.overviewChart.labels.distributionAxisLinear')},
    {value: 'priceBands', label: i18n.t('entity.overviewChart.labels.distributionAxisBands')},
  ] satisfies {value: DistributionAxisMode; label: string}[];

  const paperColor = theme.colors.palettes.white[10][100];
  const separatorColor = theme.colors.palettes.neutral[40][100];
  const baselineColor = theme.colors.palettes.neutral[30][100];
  const textPrimary = theme.colors.text.primary;
  const textSecondary = theme.colors.text.secondary;
  const textTertiary = theme.colors.text.tertiary;

  // Match the sibling charts' typography instead of arbitrary sizes.
  const fontSizeEmphasis = theme.fontSizes.text.xSmall;
  const fontSizeSmall = theme.fontSizes.text.xxSmall;
  // Same size the reference-line pills use in the other statistics charts.
  const fontSizeMarker = theme.fontSizes.text.small;
  const weightRegular = theme.fontWeights.text.default;
  const weightAlternative = theme.fontWeights.text.alternative;

  const width = Math.max(MIN_CHART_WIDTH, containerWidth ?? MIN_CHART_WIDTH);
  // The SVG scales uniformly to the container, so SVG units map to screen pixels by this.
  const scale = isNotNil(containerWidth) ? containerWidth / width : 1;
  const chartHeight = props.fillHeight ? (containerHeight ?? CHART_HEIGHT) : CHART_HEIGHT;
  const plotHeight = chartHeight - PADDING_TOP - PADDING_BOTTOM;
  const baselineY = chartHeight - PADDING_BOTTOM;
  const slotWidth = width / slotCount;

  const fractionOf = (price: number): number => (span > 0 ? (price - low) / span : 0);
  const linearSlotOf = (price: number): number =>
    Math.min(SLOT_COUNT - 1, Math.floor(fractionOf(price) * SLOT_COUNT));
  const slotOf = (price: number): number =>
    isBandMode ? getPriceBandIndex(priceBands, price) : linearSlotOf(price);
  const quintileOf = (price: number): number =>
    Math.min(QUINTILE_COUNT - 1, Math.floor(fractionOf(price) * QUINTILE_COUNT));
  const priceAtFraction = (fraction: number): number => low + span * fraction;

  const categoryOf = (item: DistributionItem): CarCategory => {
    if (isNotNil(item.car) && item.car.is_sold) {
      return isSoldIn30Days(props.priceReport, item.car) ? 'sold30' : 'sold90';
    }
    // Price-only datasets carry advertised prices exclusively.
    return 'advertised';
  };
  const categoryRank = (item: DistributionItem): number =>
    match(categoryOf(item))
      .with('advertised', () => 0)
      .with('sold30', () => 1)
      .with('sold90', () => 2)
      .exhaustive();
  const colorOf = (item: DistributionItem): string => {
    // The vehicle's own listing is highlighted regardless of its category.
    if (item.car?.isMyAd) {
      return myAdColor;
    }
    return match(categoryOf(item))
      .with('advertised', () => advertisedColor)
      .with('sold30', () => soldRecentColor)
      .with('sold90', () => soldOldColor)
      .exhaustive();
  };

  // Advertised cars stack at the bottom, sold cars on top, so a slot reads as its
  // in-advertisement base with the sold share above it. Slot and rank are computed
  // once per item (the rank involves date parsing) and buckets are filled in a
  // single pass — the render-time cost matters because hovering re-renders.
  const slots: DistributionItem[][] = Array.from({length: slotCount}, () => []);
  sortedItems
    .map((item) => ({item, slot: slotOf(item.price), rank: categoryRank(item)}))
    .sort((left, right) => left.rank - right.rank)
    .forEach(({item, slot}) => getSafely(slots, slot)?.push(item));
  const maxSlotCount = Math.max(1, ...slots.map((slotCars) => slotCars.length));
  const unitHeight = Math.min(MAX_UNIT_HEIGHT, plotHeight / maxSlotCount);
  const unitGap = getUnitGap(unitHeight);
  const drawnUnitHeight = Math.max(MIN_UNIT_HEIGHT, unitHeight - unitGap);
  const unitRadius = getUnitRadius(unitHeight);

  // Quintile shares in one pass (the index is derived once per item).
  const quintileCounts = isBandMode
    ? []
    : sortedItems.reduce(
        (counts, item) => {
          const fifth = quintileOf(item.price);
          return counts.map((count, index) => (index === fifth ? count + 1 : count));
        },
        Array.from({length: QUINTILE_COUNT}, () => 0)
      );
  const quintiles = isBandMode
    ? []
    : Array.from({length: QUINTILE_COUNT}, (_, fifth) => {
        const boundaryX = (width * fifth) / QUINTILE_COUNT;
        return {
          boundaryX,
          labelX: boundaryX + width / (QUINTILE_COUNT * 2),
          hasSeparator: fifth > 0,
          sharePercent: Math.round(((getSafely(quintileCounts, fifth) ?? 0) / total) * PERCENT),
          // The od–do boundaries already sit on the bottom axis — the top shows the
          // price span one fifth covers instead of repeating them.
          rangeLabel: `Δ ${formatThousands(span / QUINTILE_COUNT)} k`,
        };
      });

  // Band-axis visuals: one equal-width column per portal price band, tinted by the
  // same green→red ramp the quintile zones use, interpolated across all bands.
  const bandFillColors = isBandMode ? interpolatePaletteColors(bandColors, slotCount) : [];
  const bandColumns = isBandMode
    ? priceBands.map((band, bandIndex) => ({
        x: bandIndex * slotWidth,
        labelX: bandIndex * slotWidth + slotWidth / 2,
        fill: getSafely(bandFillColors, bandIndex) ?? paperColor,
        sharePercent: Math.round(((getSafely(slots, bandIndex)?.length ?? 0) / total) * PERCENT),
      }))
    : [];

  // Bottom axis in band mode — the layout math (label thinning, collision dropping,
  // cumulative percents, closing the axis) lives in the tested buildBandAxisTicks.
  const bandAxisTicks = isBandMode
    ? buildBandAxisTicks({
        bands: priceBands,
        sortedPrices: sortedItems.map((item) => item.price),
        slotWidth,
        width,
        minLabelWidth: BAND_AXIS_LABEL_MIN_WIDTH,
      }).map((tick) => ({
        tickX: clamp(tick.x, AXIS_LABEL_EDGE_INSET, width - AXIS_LABEL_EDGE_INSET),
        priceLabel: `${formatThousands(tick.price)} k`,
        percentLabel: tick.percent,
      }))
    : [];

  const units = slots.flatMap((slotItems, slotIndex) => {
    const x = slotIndex * slotWidth + 1;
    const centerX = slotIndex * slotWidth + slotWidth / 2;
    return slotItems.map((item, stackIndex) => {
      const y = baselineY - (stackIndex + 1) * unitHeight + unitGap / 2;
      return {
        key: item.car?.ad_id ?? `${x.toFixed(1)}-${y.toFixed(1)}`,
        x,
        y,
        centerX,
        color: colorOf(item),
        car: item.car,
      };
    });
  });

  const axisTicks = Array.from({length: QUINTILE_COUNT + 1}, (_, boundary) => ({
    tickX: clamp(
      (width * boundary) / QUINTILE_COUNT,
      AXIS_LABEL_EDGE_INSET,
      width - AXIS_LABEL_EDGE_INSET
    ),
    priceLabel: `${formatThousands(priceAtFraction(boundary / QUINTILE_COUNT))} k`,
    percentLabel: boundary * (PERCENT / QUINTILE_COUNT),
  }));
  const displayedAxisTicks = isBandMode ? bandAxisTicks : axisTicks;

  // On the band axis a price maps to its band's column, proportionally within it.
  // The open-ended top band has no upper boundary, so it interpolates against the
  // data maximum instead — distinct prices inside it stay distinguishable.
  const bandPositionOf = (price: number): number => {
    const bandIndex = getPriceBandIndex(priceBands, price);
    const band = getSafely(priceBands, bandIndex);
    return match(band)
      .with(Pattern.nullish, () => (price < low ? 0 : width))
      .otherwise((matchedBand) => {
        const bandEnd = matchedBand.to ?? Math.max(high, matchedBand.from + 1);
        const bandFraction =
          bandEnd > matchedBand.from
            ? (price - matchedBand.from) / (bandEnd - matchedBand.from)
            : 1;
        return (bandIndex + clamp(bandFraction, 0, 1)) * slotWidth;
      });
  };

  // The line sits at the price's true position, clamped only to the chart edges: a
  // price below the cheapest car pins it to the far left, above the priciest far right.
  const markerX = isNotNil(props.myPrice)
    ? clamp(
        isBandMode ? bandPositionOf(props.myPrice) : width * fractionOf(props.myPrice),
        0,
        width
      )
    : null;
  const zoneTopY = PADDING_TOP - ZONE_TOP_OFFSET;
  const markerPillY = zoneTopY + MARKER_PILL_TOP_INSET;

  // Screen-pixel position of the car tooltip, anchored above the hovered unit.
  // The measured size keeps it clamped inside the chart, whose card clips overflow.
  // The overlay stays permanently mounted (hidden when nothing is hovered) so the
  // size observer is attached from the start and the measurement is always live.
  const containerScreenWidth = containerWidth ?? width;
  const tooltipHalfWidth = (tooltipWidth ?? 0) / 2;
  const tooltipLeft = clamp(
    (hovered?.anchorX ?? 0) * scale,
    tooltipHalfWidth,
    Math.max(tooltipHalfWidth, containerScreenWidth - tooltipHalfWidth)
  );
  const tooltipTop = Math.max((hovered?.anchorY ?? 0) * scale - TOOLTIP_MARGIN, tooltipHeight ?? 0);

  return (
    <VStack height={props.fillHeight ? '100%' : undefined}>
      {/* Statistics-page charts carry no title of their own — dashboard cards do */}
      <Show when={!props.isTitleHidden}>
        <Align center>
          <Heading size={4}>{i18n.t('entity.overviewChart.labels.priceDistributionTitle')}</Heading>
        </Align>
        <Space vertical={2} />
        <Align center>
          <Text size="xSmall" color="secondary" align="center">
            {i18n.t('entity.overviewChart.labels.priceDistributionDescription')}
          </Text>
        </Align>
        <Space vertical={4} />
      </Show>
      {/* Linear ↔ portal-band axis toggle: a per-user viewing preference, so it sits
          by the chart itself rather than in the page-level chart-type switch. It stays
          mounted through refetches (no layout jump) and hides when the chart cannot
          draw — a toggle above the empty state would be a dead control. */}
      <Show when={isBandAxisUsable && hasEnoughData}>
        <Hide inPrinter>
          <HStack justify="flex-end">
            <Segment
              value={axisMode}
              options={axisModeOptions}
              onChange={setAxisMode}
              data-testid={suffixTestId('axisModeSwitch', props)}
            />
          </HStack>
          <Space vertical={2} />
        </Hide>
      </Show>
      <Box
        ref={ref}
        position="relative"
        flexGrow={props.fillHeight ? 1 : undefined}
        minHeight={CHART_MIN_HEIGHT_UNITS}
        data-testid={suffixTestId('wrapper', props)}
      >
        {/* One vehicle (or one shared price) gives the distribution no axis to
            spread over — an explanatory empty state replaces the chart. DataStatus
            owns the loading/empty/content switching and centres both states. */}
        <DataStatus
          isLoading={props.isLoading}
          isEmpty={!hasEnoughData}
          emptyMessage={i18n.t('entity.overviewChart.labels.priceDistributionEmptyTitle')}
          emptySubheadline={i18n.t('entity.overviewChart.labels.priceDistributionEmptyDescription')}
          minHeight={props.fillHeight ? '100%' : CHART_MIN_HEIGHT_UNITS}
          data-testid={props['data-testid']}
        >
          <svg
            width="100%"
            height={props.fillHeight ? '100%' : undefined}
            viewBox={`0 0 ${width} ${chartHeight}`}
            role="img"
            onMouseLeave={() => setHovered(null)}
            data-testid={suffixTestId('svg', props)}
          >
            {/* Backgrounds: tinted band columns or the five quintile zones */}
            <Show when={isBandMode}>
              <BandColumns
                columns={bandColumns}
                slotWidth={slotWidth}
                zoneTopY={zoneTopY}
                baselineY={baselineY}
                data-testid={props['data-testid']}
              />
            </Show>
            <Show when={!isBandMode}>
              <QuintileZones
                quintiles={quintiles}
                zoneWidth={width / QUINTILE_COUNT}
                zoneTopY={zoneTopY}
                baselineY={baselineY}
                zoneFills={bandColors}
                fallbackFill={paperColor}
                data-testid={props['data-testid']}
              />
            </Show>

            {/* One rectangle per car, stacked bottom-up in its price slot.
                  Hover reveals the car tooltip only when real cars back the units. */}
            {units.map((unit) => {
              const hoveredCar = unit.car;
              return (
                <rect
                  key={unit.key}
                  x={unit.x}
                  y={unit.y}
                  width={slotWidth - 2}
                  height={drawnUnitHeight}
                  rx={unitRadius}
                  fill={unit.color}
                  cursor={isNotNil(hoveredCar) ? 'pointer' : undefined}
                  onMouseEnter={
                    isNotNil(hoveredCar)
                      ? () => setHovered({car: hoveredCar, anchorX: unit.centerX, anchorY: unit.y})
                      : undefined
                  }
                  onMouseLeave={isNotNil(hoveredCar) ? () => setHovered(null) : undefined}
                />
              );
            })}

            {/* Baseline + the two-row price/percent axis */}
            <DistributionAxis
              width={width}
              baselineY={baselineY}
              ticks={displayedAxisTicks}
              boundaryTickXs={priceBands.map((_, bandIndex) => bandIndex * slotWidth)}
              isBandMode={isBandMode}
              data-testid={props['data-testid']}
            />

            {/* "Tento vůz" marker: vertical line, baseline dot, pill and beak */}
            <DistributionMarker
              markerX={markerX}
              width={width}
              pillY={markerPillY}
              baselineY={baselineY}
              color={markerColor}
              paperColor={paperColor}
              label={markerLabel}
              data-testid={props['data-testid']}
            />

            {/* Cumulative-percent explainer bottom-left, mirroring the square legend */}
            <Show when={isBandMode}>
              <text
                x={0}
                y={chartHeight - 5}
                fontSize={fontSizeSmall}
                fontWeight={weightRegular}
                fill={textTertiary}
              >
                {i18n.t('entity.overviewChart.labels.priceDistributionBandsAxisHint')}
              </text>
            </Show>

            {/* Legend bottom-right */}
            <rect
              x={width - LEGEND_WIDTH}
              y={chartHeight - 12}
              width={9}
              height={7}
              rx={2}
              fill={advertisedColor}
            />
            <text
              x={width - LEGEND_WIDTH + 13}
              y={chartHeight - 5}
              fontSize={fontSizeSmall}
              fontWeight={weightRegular}
              fill={textTertiary}
            >
              {i18n.t('entity.overviewChart.labels.priceDistributionLegend', {
                total: formatNumber(total),
              })}
            </text>
          </svg>
        </DataStatus>

        {/* Car popover — the same tooltip as the statistics chart, rendered as an HTML
            overlay (not inside the SVG) so it is never clipped by the chart bounds.
            It must live OUTSIDE DataStatus: useElementSize attaches its observer only
            on the component's first mount, so the overlay has to be in the DOM from
            the start (hidden), even while the loading/empty state shows. */}
        <TooltipOverlay
          ref={tooltipRef}
          $left={tooltipLeft}
          $top={tooltipTop}
          $isVisible={isNotNil(hovered)}
        >
          {isNotNil(hovered) ? (
            <CarTooltip
              priceReport={props.priceReport}
              car={hovered.car}
              showShadow
              data-testid={suffixTestId('carTooltip', props)}
            />
          ) : null}
        </TooltipOverlay>
      </Box>
    </VStack>
  );
}

interface TooltipOverlayProps {
  $left: number;
  $top: number;
  $isVisible: boolean;
}

/**
 * Pixel-anchored overlay for the car tooltip. The frequently changing position is
 * injected via the style attribute (the styled-components pattern for per-render
 * values), so no inline style prop appears in the JSX.
 */
const TooltipOverlay = styled.div.attrs<TooltipOverlayProps>(({$left, $top, $isVisible}) => ({
  style: {
    left: $left,
    top: $top,
    visibility: $isVisible ? ('visible' as const) : ('hidden' as const),
  },
}))<TooltipOverlayProps>`
  position: absolute;
  /* Natural content width so mileage and price in the tooltip never clip */
  width: max-content;
  transform: translate(-50%, -100%);
  pointer-events: none;
  z-index: 1;
`;
